import { useEffect } from 'react';
import { generatePath, useMatch, useNavigate } from 'react-router-dom';
import { PermissionEnum } from '@ui/src';
import { CASP } from '@ui/src/components/transactions-sidepanels/types';
import {
  useLazyGetCustomersPspIntegrationsQuery,
  useLazyGetCustomersPspPartnersQuery,
} from '../../../customers/store/services/customers';
import { useLazyGetCustomersCASPQuery } from '../../../../store/dictionaries/services';
import { PspIntegration, OrganizationType, PspPartner } from '../../types';

import ROUTES from '../../../../routes';

const useExternalIntegration = ({
  activePermissions,
  pspIntegrations,
  pspPartners,
  casp,
  customerId,
  type,
}: {
  activePermissions?: PermissionEnum[];
  pspIntegrations?: PspIntegration[];
  pspPartners?: PspPartner[];
  casp?: CASP[];
  customerId?: string;
  type: OrganizationType;
}) => {
  const individualMatch = useMatch(
    ROUTES.CUSTOMERS_INDIVIDUAL_EXTERNAL_ACCOUNTS,
  );
  const corporateMatch = useMatch(ROUTES.CUSTOMERS_CORPORATE_EXTERNAL_ACCOUNTS);

  const [getPspPartners] = useLazyGetCustomersPspPartnersQuery();
  const [getCustomersCasp] = useLazyGetCustomersCASPQuery();
  const [getPspIntegrations] = useLazyGetCustomersPspIntegrationsQuery();

  const navigate = useNavigate();

  useEffect(() => {
    if (
      activePermissions?.includes(PermissionEnum.PSP_LIST) &&
      !pspIntegrations
    ) {
      getPspIntegrations();
    }
  }, [activePermissions, pspIntegrations]);

  useEffect(() => {
    if (!pspPartners) {
      getPspPartners();
    }
  }, [pspPartners]);

  useEffect(() => {
    if (!casp) {
      getCustomersCasp();
    }
  }, [casp]);

  useEffect(() => {
    if (customerId) {
      if (type === OrganizationType.INDIVIDUAL && individualMatch) {
        if (
          activePermissions?.includes(
            PermissionEnum.PSP_INTEGRATION_ACCOUNT_LIST,
          )
        ) {
          navigate(
            generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_EXTERNAL_ACCOUNTS_LIST, {
              customerId,
            }),
          );
        } else if (
          !activePermissions?.includes(
            PermissionEnum.PSP_INTEGRATION_ACCOUNT_LIST,
          ) &&
          activePermissions?.includes(
            PermissionEnum.CUSTOMERS_EXTERNAL_ORGANIZATIONS_LIST,
          )
        ) {
          navigate(
            generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_EXTERNAL_LINKED_CLIENTS, {
              customerId,
            }),
          );
        }
      } else if (type === OrganizationType.CORPORATE && corporateMatch) {
        if (
          activePermissions?.includes(
            PermissionEnum.PSP_INTEGRATION_ACCOUNT_LIST,
          )
        ) {
          navigate(
            generatePath(ROUTES.CUSTOMERS_CORPORATE_EXTERNAL_ACCOUNTS_LIST, {
              customerId,
            }),
          );
        } else if (
          !activePermissions?.includes(
            PermissionEnum.PSP_INTEGRATION_ACCOUNT_LIST,
          ) &&
          activePermissions?.includes(
            PermissionEnum.CUSTOMERS_EXTERNAL_ORGANIZATIONS_LIST,
          )
        ) {
          navigate(
            generatePath(ROUTES.CUSTOMERS_CORPORATE_EXTERNAL_LINKED_CLIENTS, {
              customerId,
            }),
          );
        }
      }
    }
  }, [type, individualMatch, corporateMatch, customerId]);
};

export default useExternalIntegration;
